import css from './index.module.scss';
import { useLang } from '@/i18n';
import { useRouter } from 'next/router';
import ReCAPTCHA from 'react-google-recaptcha';
import { Select, Switch, message, Checkbox, Row, Col } from 'antd';
import { useEffect, useState } from 'react';
import type { CooperationProps } from './interface';
import { getStatesList, getCitiesList, createDealer } from '@/service';
import { INIT_COUNTRY_MAP } from '@/constants/quote';
import type { CityListRes, StateListRes } from './interface';
import { ENGLISH_LANG, rpcMap } from '@/constants';
import gtm from '@/utils/gtm';
import EcoSpace from 'eco-design/base/eco-space';
interface OptionProps {
    key: number;
    label?: string;
    value?: string | boolean;
    text?: string;
    phoneCode?: string;
}

interface FormItemProps {
    placeholder?: string;
    key: string;
    type: 'input' | 'select' | 'textarea' | 'recaptcha' | 'switch' | 'checkbox' | 'button' | 'radio';
    required?: boolean;
    tip?: string;
    long?: boolean; // UI长文本 独占100%宽度
    options?: OptionProps[];
    disable?: boolean; // true 隐藏 / false 显示
    max?: number; // 最大长度
    rows?: number; // 行数
    text?: string; // 文本
}

interface FormDataProps {
    firstName: string;
    lastName: string;
    country: string | undefined;
    state: string | undefined;
    city: string | undefined;
    zipCode: string;
    phone: string;
    email: string;
    companyName: string;
    companyAddress: string;
    licensedElectricianConstructor: boolean | undefined;
    productCategory: string;
    message: string;
    recaptchaResponse: string;
    agreed: string[];
}

interface FormTipProps {
    firstName: boolean;
    lastName: boolean;
    country: boolean;
    state: boolean;
    zipCode: boolean;
    phone: boolean;
    email: boolean;
    companyName: boolean;
    productCategory: boolean;
    recaptchaResponse: boolean;
    agreed: boolean;
    companyAddress: boolean;
    message: boolean;

}

interface SelectOpenProps {
    country: boolean;
    state: boolean;
    city: boolean;
    productCategory: boolean;
}

const RegMap: { [key: string]: RegExp } = {
    firstName: /^((?!\d|\+|&|=|\_|'|-|,|<|>|\.{2,}).){1,30}$/,
    lastName: /^((?!\d|\+|&|=|\_|'|-|,|<|>|\.{2,}).){1,30}$/,
    zipCode: /^[A-Za-z0-9]{0,10}$/,
    phone: /^[0-9+]{7,20}$/,
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
};

const Form: React.FC<CooperationProps> = ({ countryList, type }) => {
    const SECRET_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_REGISTER_SECRET_KEY;
    const { t } = useLang('business.I');
    const { locale = 'us' } = useRouter();
    const [form, setForm] = useState({} as FormDataProps);
    const [tipForm, setTipForm] = useState({} as FormTipProps);
    const [selectOpen, setSelectOpen] = useState({
        country: false,
        state: false,
        city: false,
        productCategory: false,
    } as SelectOpenProps);
    const hl = rpcMap[ENGLISH_LANG.includes(locale) ? 'en' : locale];
    const [key, setKey] = useState(new Date().getTime());
    const [agreed, setAgreed] = useState(true);
    const { t: s } = useLang('business');
    const items_static: FormItemProps[] = [
        {
            placeholder: t('label_first_name'),
            key: 'firstName',
            type: 'input',
            required: true,
            tip: t('tip_first_name'),
        },
        {
            placeholder: t('label_last_name'),
            key: 'lastName',
            type: 'input',
            required: true,
            tip: t('tip_last_name'),
        },
        {
            placeholder: t('label_email'),
            key: 'email',
            type: 'input',
            required: true,
            tip: t('tip_email'),
        },
        {
            placeholder: t('label_country'),
            key: 'country',
            type: 'select',
            required: true,
            tip: t('tip_country'),
            options: [] as OptionProps[],
        },
        {
            placeholder: t('label_state'),
            key: 'state',
            type: 'select',
            required: true,
            tip: t('tip_state'),
            options: [] as OptionProps[],
        },
        {
            placeholder: t('label_city'),
            key: 'city',
            type: 'select',
            options: [] as OptionProps[],
        },
        {
            placeholder: t('label_zip_code'),
            key: 'zipCode',
            type: 'input',
            required: true,
            tip: t('tip_zip_code'),
        },
        {
            placeholder: t('label_phone_number'),
            key: 'phone',
            type: 'input',
            required: true,
            tip: t('tip_phone_number'),
        },

        {
            placeholder: t('label_company_name'),
            key: 'companyName',
            type: 'input',
            required: true,
            tip: t('tip_company_name'),
        },
        {
            placeholder: t('label_company_address'),
            key: 'companyAddress',
            type: 'input',
            required: true,
            tip: t('tip_company_address'),
        },
        {
            placeholder: t('label_product_category'),
            key: 'productCategory',//感兴趣的产品
            type: 'select',
            required: true,
            long: true,
            tip: t('tip_product_category'),
            options: Array.from({ length: 19 }, (_, i) => ({
                key: i + 1,
                label: t(`interested_product_category_${i + 1}`),
                value: t(`interested_product_category_${i + 1}`),
            })) as OptionProps[],
        },

        {
            placeholder: t('label_message'),
            key: 'message',
            type: 'textarea',
            long: true,
            rows: 10,
            required: true,
            tip: t('tip_message')
        },
        {
            placeholder: t('label_agreed'),
            key: 'agreed',
            type: 'checkbox',
            required: true,
            long: true,
            tip: t('tip_agreed'),
            options: [{
                key: 1,
                value: '1',
                label: 'I have read and agree to the  <a href="https://account.ecoflow.com/agreement/en-us/PrivacyNoticeDealerContact.html" target="_blank" rel="noopener noreferrer">Privacy Notice</a> below.'
            }]
        },
        {
            key: 'recaptchaResponse',
            type: 'recaptcha',
            required: true,
            tip: t('tip_recaptcha'),
        },
        {
            key: 'submit',
            type: 'button',
            long: true,
            text: t('button_submit'),
        },
    ];
    const typeList: { [key: string]: any } = {
        [s('tab_title_1')]: 'Power stations',
        [s('tab_title_2')]: 'Home backup solution',
        [s('tab_title_3')]: 'Power stations',
        [s('tab_title_4')]: 'Home solar battery solution',
        [s('tab_title_5')]: 'Home solar battery solution',
    }
    useEffect(() => {
        setForm({
            ...form,
            productCategory: typeList[type],
        });
    }, [type])

    const [items, setItems] = useState<FormItemProps[]>(items_static);

    const handleFormChange = (e: any, key: string, type: string) => {
        if (['input', 'textarea'].includes(type)) {
            return setForm({
                ...form,
                [key]: e.target.value,
            });
        }
        if (['select', 'switch', 'recaptcha', 'checkbox'].includes(type)) {
            if (type === 'select') {
                if (key === 'country') {
                    form.state = undefined;
                    form.city = undefined;
                }
                if (key === 'state') {
                    form.city = undefined;
                }
            }
            if (tipForm[key as keyof FormTipProps]) {
                setTipForm({
                    ...tipForm,
                    [key]: false,
                });
            }

            if (key === 'agreed' && e[0] === '1') {
                setAgreed(true)
            } else if (key === 'agreed' && e[0] !== '1') {
                setAgreed(false)
            }

            return setForm({
                ...form,
                [key]: e,
            });
        }

        if (type === 'button' && key === 'submit') {
            return handleSubmit();
        }

    };

    const handleOnFocus = (key: string) => {
        if (tipForm[key as keyof FormTipProps]) {
            setTipForm({
                ...tipForm,
                [key]: false,
            });
        }
    };

    const handleValidate = () => {
        let tipObj: FormTipProps = {
            firstName: false,
            lastName: false,
            country: false,
            state: false,
            zipCode: false,
            phone: false,
            email: false,
            companyName: false,
            productCategory: false,
            recaptchaResponse: false,
            agreed: false,
            companyAddress: false,
            message: false,
        };
        const needValiProps: string[] = items?.filter((item: FormItemProps) => item.required)?.map((item: FormItemProps) => item.key);
        needValiProps?.map((key: string) => {
            const value = form[key as keyof FormDataProps];
            if (key === 'agreed' && value?.[0] !== '1') {
                tipObj[key] = true;
                return
            }
            tipObj[key] = Boolean(!value || (RegMap[key] && !RegMap[key].test(String(value))));
        });
        setTipForm(tipObj);
        return Object.values(tipObj)?.every((v: boolean) => !v);
    };

    const handleSubmit = async () => {
        if (handleValidate()) {
            const post = JSON.parse(JSON.stringify(form))
            post.agreed = agreed
            post.version = 'V2'
            const res = await createDealer(post);
            if (res?.code === '0') {
                gtm.push({
                    event: 'uaEvent',
                    event_name: "pre_order_submit",
                    button_name: t('button_submit'),
                    form_type: t('title'),
                    form_info: "business",
                    status: 'success',
                    __btn_id1: `business-I .button`,
                    __btn_id2: t('button_submit'),
                    __btn_type: 'common',
                });
                message.success(res.message || 'Success');
                setForm((prev) => {
                    return { country: prev.country } as FormDataProps;
                });
                setTipForm((_) => {
                    return {} as FormTipProps;
                });
                setKey(new Date().getTime());
            } else {
                gtm.push({
                    event: 'uaEvent',
                    event_name: "pre_order_submit",
                    button_name: t('button_submit'),
                    form_type: t('title'),
                    form_info: "business",
                    status: 'fail',
                    __btn_id1: `business-I .button`,
                    __btn_id2: t('button_submit'),
                    __btn_type: 'common',
                });
                message.error(res.message);
            }
        }
    };

    const getStates = async (countryId: number) => {
        try {
            const res: StateListRes = await getStatesList({
                countryId,
                page: 1,
                size: 300,
            });
            if (res.code === '0') {
                const stateList: OptionProps[] = res.data?.content?.map((item: any) => ({
                    key: item.id,
                    label: item.name,
                    value: item.name,
                }));
                setItems((prevItems: FormItemProps[]) => {
                    const newItems = JSON.parse(JSON.stringify(prevItems));
                    const targetState = newItems.find((item: FormItemProps) => item.key === 'state');
                    if (targetState) {
                        targetState.options = stateList;
                    }
                    const targetCity = newItems.find((item: FormItemProps) => item.key === 'city');
                    if (targetCity) {
                        targetCity.options = [] as OptionProps[];
                    }
                    return newItems;
                });
            } else {
                message.error(res.message);
            }
        } catch (err) {
            console.log(err);
        }
    };

    const getCity = async (stateId: number) => {
        try {
            const res: CityListRes = await getCitiesList({
                stateId,
                page: 1,
                size: 3000,
            });
            if (res.code === '0') {
                const cityList: OptionProps = res.data?.content?.map((item: any) => ({
                    key: item.id,
                    label: item.name,
                    value: item.name,
                }));
                setItems((prevItems: FormItemProps[]) => {
                    const newItems = JSON.parse(JSON.stringify(prevItems));
                    const targetCity = newItems.find((item: FormItemProps) => item.key === 'city');
                    if (targetCity) {
                        targetCity.options = cityList;
                    }
                    return newItems;
                });
            } else {
                message.error(res.message);
            }
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        if (countryList?.length > 0) {
            const countryOptions: OptionProps[] = countryList
                ?.filter((item: any) => item.text && item.key && item.phoneCode)
                .map(
                    (country: any) =>
                        country?.text && {
                            key: country.key,
                            label: country.text,
                            value: country.text,
                        },
                );
            const newItems = JSON.parse(JSON.stringify(items));
            const target = newItems.find((item: FormItemProps) => item.key === 'country');
            if (target) {
                target.options = countryOptions;
            }
            setItems(newItems);
            const targetCountry = countryOptions?.find((item: any) => item.value === INIT_COUNTRY_MAP[locale]?.name);
            setForm({
                ...form,
                country: form?.country || targetCountry?.value || '',
                agreed: ['1'],
                productCategory: 'Power stations',
            });
        }
    }, [countryList]);

    useEffect(() => {
        const targetCountry = countryList?.find((country: any) => country.text === form.country);
        if (targetCountry) {
            getStates(targetCountry.key);
        }
    }, [form.country]);

    useEffect(() => {
        const stateList: OptionProps[] = items.find((item: FormItemProps) => item.key === 'state')?.options || [];
        const targetState = stateList?.find((state: any) => state.value === form.state);
        if (targetState) {
            getCity(targetState.key);
        }
    }, [form.state]);



    return (
        <div className={`${css.form} business-I  GtmModuleData-section`}
            id={`business-I`}
            data-section-id={`business-I`}
            data-section-title={`business-I`}>
            <EcoSpace />
            <div className="info">
                <h2 className="title">{t('title')}</h2>
                <p className="desc">{t('desc')}</p>
            </div>
            <ul className="form-box" key={key + '0'}>
                {items?.map((item: FormItemProps) => (
                    <li className={`form-item${item.long ? ' long' : ''}${item.disable ? ' disable' : ''} form-item-${item.type}`} key={item.key}>
                        {item.type === 'input' && (
                            <>
                                {item.required && !form[item.key as keyof FormDataProps] && <span className="red-star">*</span>}
                                <input
                                    type="text"
                                    className="item-cont input"
                                    placeholder={(item?.required ? '  ' : '') + item.placeholder}
                                    value={form[item.key as keyof FormDataProps]}
                                    onChange={(e: any) => handleFormChange(e, item.key, item.type)}
                                    onFocus={() => handleOnFocus(item.key)}
                                />
                            </>
                        )}
                        {item.type === 'select' && (
                            <Select
                                className={`item-cont select${item?.required ? ' required' : ''}`}
                                placeholder={(item?.required ? '  ' : '') + item.placeholder}
                                value={form[item.key as keyof FormDataProps]}
                                showSearch
                                getPopupContainer={(triggerNode: any) => triggerNode.parentNode || document.body}
                                onDropdownVisibleChange={(open: boolean) =>
                                    setSelectOpen({
                                        ...selectOpen,
                                        [item.key]: open,
                                    })
                                }
                                suffixIcon={
                                    <svg
                                        className="select-arrow"
                                        style={{
                                            transform: `rotate(${selectOpen[item.key as keyof SelectOpenProps] ? '180deg' : '0deg'})`,
                                        }}
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="17"
                                        viewBox="0 0 16 17"
                                        fill="none"
                                    >
                                        <path
                                            d="M13.641 6.75651L8.30766 12.0898L2.97432 6.75651"
                                            stroke="#000"
                                            stroke-width="1.4"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                }
                                dropdownClassName="ef-select-item"
                                onChange={(e: any) => handleFormChange(e, item.key, item.type)}
                            >
                                {item.options?.map((option: OptionProps) => (
                                    <option value={option.value} key={item.key + '_' + option.value + '_' + option.key}>
                                        {option.label}
                                    </option>
                                ))}
                            </Select>
                        )}
                        {item.type === 'textarea' && (
                            <>
                                {item.required && !form[item.key as keyof FormDataProps] && <span className="red-star">*</span>}
                                <textarea
                                    className={`item-cont textarea${item?.required ? ' required' : ''}`}
                                    placeholder={(item?.required ? '  ' : '') + item.placeholder}
                                    value={form[item.key as keyof FormDataProps]}
                                    onChange={(e: any) => handleFormChange(e, item.key, item.type)}
                                    onFocus={() => handleOnFocus(item.key)}
                                    maxLength={item?.max || 2000}
                                    rows={item?.rows || 3}
                                />
                                <p className="text-num">
                                    {form[item.key as keyof FormDataProps] ? form[item.key as keyof FormDataProps]?.length : 0}
                                    {' / '}
                                    {item?.max || 2000}
                                </p>
                            </>
                        )}
                        {item.type === 'switch' && (
                            <div className="item-cont switch">
                                <span className="item-label">{(item?.required ? '* ' : '') + item.placeholder}</span>
                                <Switch
                                    className="switch-box"
                                    checked={form.licensedElectricianConstructor}
                                    onChange={(e: any) => handleFormChange(e, item.key, item.type)}
                                />
                            </div>
                        )}
                        {item.type === 'checkbox' && (
                            <Checkbox.Group value={form[item.key as keyof FormDataProps]} onChange={(e: any) => handleFormChange(e, item.key, item.type)}>
                                <Row>
                                    {item.options?.map((item: any, index: number) => {
                                        return (
                                            <Col key={index}>
                                                <Checkbox value={item.value}>
                                                    <div className='Privacy-Notice' dangerouslySetInnerHTML={{
                                                        __html: item.label,
                                                    }}></div>
                                                </Checkbox>
                                            </Col>
                                        );
                                    })}
                                </Row>
                            </Checkbox.Group>
                        )}
                        {item.type === 'recaptcha' && (
                            <div className="item-cont recaptcha">
                                <ReCAPTCHA key={key} hl={hl} sitekey={SECRET_KEY || ''} onChange={(e: any) => handleFormChange(e, item.key, item.type)} />
                            </div>
                        )}
                        {item.type === 'button' && (
                            <button className={`item-cont button  GtmModuleData-btn-common ${!agreed ? 'disable' : ''}`} data-btn-id={t('button_submit')} onClick={() => handleFormChange('', item.key, item.type)}>
                                {item?.text}
                            </button>
                        )}
                        {item?.tip && tipForm[item.key as keyof FormTipProps] && <p className="form-item-tip">{item.tip}</p>}
                    </li>
                ))}
            </ul>
            <EcoSpace />
        </div>
    );
};

export default Form;
